import { useCallback, useEffect, useState } from "react";

// ---------------------------------------------------------------
// Per-item "sync again in N seconds" cooldown.
// Kept in a module-level map (survives closing/reopening a modal) and
// mirrored to sessionStorage (survives a page refresh in the same tab).
// ---------------------------------------------------------------

export const SYNC_COOLDOWN_MS = 60 * 1000;

const STORAGE_KEY = "nms_sync_cooldowns";
const memory = new Map();

const readAll = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
};

const readUntil = (key) => {
  if (memory.has(key)) return memory.get(key);
  const until = Number(readAll()[key]) || 0;
  memory.set(key, until);
  return until;
};

const writeUntil = (key, until) => {
  memory.set(key, until);
  try {
    const all = readAll();
    const now = Date.now();
    Object.keys(all).forEach((k) => {
      if (Number(all[k]) < now) delete all[k];
    });
    all[key] = until;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    // storage unavailable (private mode etc.) — memory copy still works
  }
};

/**
 * const [secondsLeft, startCooldown] = useSyncCooldown("device:3");
 * startCooldown()        -> hide sync for 60s
 * startCooldown(30000)   -> hide sync for 30s
 */
export function useSyncCooldown(key) {
  const [now, setNow] = useState(() => Date.now());

  const until = key ? readUntil(key) : 0;
  const remaining = Math.max(0, Math.ceil((until - now) / 1000));
  const active = remaining > 0;

  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active, key]);

  const start = useCallback(
    (ms = SYNC_COOLDOWN_MS) => {
      if (!key) return;
      writeUntil(key, Date.now() + ms);
      setNow(Date.now());
    },
    [key]
  );

  return [remaining, start];
}

export const fmtCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;